/**
 * NextCallObjectives
 *
 * Allows operators to define objectives for the next call.
 * Displays a dynamic list of text inputs with add/remove controls.
 * Persists objectives to backend with debouncing.
 */
import { Plus, Target, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useSetNextCallObjectivesMutation } from '@/services/api/treatmentCheckInV2Api';

import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';

// ── Props ────────────────────────────────────────────────────────────────────

interface NextCallObjectivesProps {
  checkInId: string;
  matterId: string;
  manualObjectives?: string[];
  legacyObjectives?: string[];
  suggestedObjectives?: string[];
  readOnly?: boolean; // true when finalized
}

const DEBOUNCE_MS = 1000;

function areObjectivesEqual(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

// ── Component ────────────────────────────────────────────────────────────────

export const NextCallObjectives = ({
  checkInId,
  matterId,
  manualObjectives,
  legacyObjectives,
  suggestedObjectives,
  readOnly = false,
}: NextCallObjectivesProps) => {
  const persistedObjectives = manualObjectives ?? legacyObjectives ?? [];
  const [objectives, setObjectives] = useState<string[]>(persistedObjectives);
  const [setNextCallObjectives] = useSetNextCallObjectivesMutation();
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFirstRender = useRef(true);
  const isSyncingFromProps = useRef(false);
  const isDirty = useRef(false);
  const editVersion = useRef(0);

  useEffect(() => {
    setObjectives((prev) => {
      if (isDirty.current || areObjectivesEqual(prev, persistedObjectives)) {
        return prev;
      }
      isSyncingFromProps.current = true;
      return persistedObjectives;
    });
  }, [persistedObjectives]);

  // Debounced persistence: save to backend whenever objectives change
  useEffect(() => {
    // Skip the initial render to avoid persisting on mount
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return undefined;
    }
    if (isSyncingFromProps.current) {
      isSyncingFromProps.current = false;
      return undefined;
    }
    if (!isDirty.current) return undefined;
    if (readOnly) return undefined;

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    const scheduledVersion = editVersion.current;
    debounceTimer.current = setTimeout(() => {
      setNextCallObjectives({ matterId, checkInId, objectives })
        .unwrap()
        .then(() => {
          if (editVersion.current === scheduledVersion) {
            isDirty.current = false;
          }
        })
        .catch(() => {
          // Keep local dirty state so the operator doesn't lose unsaved edits.
        });
    }, DEBOUNCE_MS);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [objectives, matterId, checkInId, readOnly, setNextCallObjectives]);

  const handleAdd = useCallback(() => {
    isDirty.current = true;
    editVersion.current += 1;
    setObjectives((prev) => [...prev, '']);
  }, []);

  const handleRemove = useCallback((index: number) => {
    isDirty.current = true;
    editVersion.current += 1;
    setObjectives((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleChange = useCallback((index: number, value: string) => {
    isDirty.current = true;
    editVersion.current += 1;
    setObjectives((prev) => prev.map((obj, i) => (i === index ? value : obj)));
  }, []);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm">
          <Target className="size-4" />
          Next Call Objectives
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {(suggestedObjectives ?? []).length > 0 && (
          <div className="space-y-2 rounded-lg border border-border/60 bg-muted/20 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Carrying Forward
            </p>
            <div className="space-y-1">
              {suggestedObjectives?.map((objective, index) => (
                <p key={`${objective}-${index}`} className="text-sm font-medium">
                  {objective}
                </p>
              ))}
            </div>
          </div>
        )}

        {objectives.length === 0 && readOnly && (
          <p className="text-sm text-muted-foreground">
            No objectives set for the next call.
          </p>
        )}

        {objectives.map((objective, index) => (
          <div key={index} className="flex items-center gap-2">
            <span className="shrink-0 text-xs font-medium text-muted-foreground">
              {index + 1}.
            </span>
            {readOnly ? (
              <p className="text-sm font-medium">{objective}</p>
            ) : (
              <>
                <Input
                  value={objective}
                  onChange={(e) => handleChange(index, e.target.value)}
                  placeholder="Add a manual next-call objective..."
                  className="h-8 text-sm"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  className="size-8 shrink-0 p-0 text-muted-foreground hover:text-destructive"
                  onClick={() => handleRemove(index)}
                >
                  <Trash2 className="size-3.5" />
                </Button>
              </>
            )}
          </div>
        ))}

        {!readOnly && (
          <Button
            variant="outline"
            size="sm"
            className="mt-1 gap-1.5"
            onClick={handleAdd}
          >
            <Plus className="size-3" />
            Add objective
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
